import React, { useState } from 'react';
import { PenLine, FileText, Loader2, CheckCircle2, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { SignaturePad } from '../SignaturePad';
import { inputCls } from './shared';

const errMsg = (e) => e?.response?.data?.detail || 'Erreur';
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

export const ContractPdfButton = ({ p }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/partnerships/${p.id}/contract/pdf`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      link.download = `contrat_partenariat_${(p.partner?.name || 'partenaire').replace(/\s+/g, '_')}_v${p.contract_version || 1}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error('Contrat indisponible'); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={download} disabled={busy} className="rounded-xl border-[#2B3A2C]/25" data-testid={`contract-pdf-${p.id}`}>
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Contrat PDF
    </Button>
  );
};

export const SignatureStatus = ({ p, own }) => {
  const partnerId = p.partner?.id;
  const sigs = p.signatures || {};
  const Row = ({ id, name }) => {
    const s = sigs[id];
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
        data-testid={`signature-status-${p.id}-${id === own ? 'own' : 'partner'}`}>
        {s ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
        {name} : {s ? `signé le ${fmt(s.signed_at)}` : 'à signer'}
      </span>
    );
  };
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <Row id={own} name="Votre agence" />
      <Row id={partnerId} name={p.partner?.name} />
    </div>
  );
};

export const ContractSignDialog = ({ target, onClose, onDone }) => {
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const { p, forPartner } = target;

  const save = async (dataUrl) => {
    if (!name.trim()) { toast.error('Indiquez le nom du signataire'); return; }
    setBusy(true);
    try {
      await api.post(`/partnerships/${p.id}/contract/sign`, {
        signature_base64: dataUrl, signer_name: name.trim(), signer_title: title.trim() || null, for_partner: forPartner,
      });
      toast.success('Contrat signé');
      setName(''); setTitle(''); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="contract-sign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C] flex items-center gap-2">
            <PenLine className="w-5 h-5 text-[#B85C38]" /> {forPartner ? `Signature de ${p.partner?.name}` : 'Signer le contrat'}
          </DialogTitle>
          <DialogDescription>
            Contrat version {p.contract_version || 1} · commission {p.commission_split}% pour l'agence qui recommande.
            {forPartner ? ' Faites signer le représentant du partenaire sur cet appareil.' : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-gray-600">Nom du signataire *
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="contract-signer-name-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Fonction
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Gérant, Directeur…" className={inputCls} data-testid="contract-signer-title-input" />
          </label>
        </div>
        <div className={busy ? 'pointer-events-none opacity-60' : ''} data-testid="contract-signature-pad">
          <SignaturePad onSave={save} onCancel={onClose} width={380} height={170} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
